import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlayIcon,
  ClockIcon,
  ServerStackIcon,
  CircleStackIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import { getMigrationStatus, runMigrations } from "../api/migrations";

// ── Helpers ──────────────────────────────────────────────────────────────────
const StatusBadge = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
      ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
    }`}
  >
    {ok ? (
      <CheckCircleIconSolid className="w-3 h-3" />
    ) : (
      <ExclamationCircleIcon className="w-3 h-3" />
    )}
    {label}
  </span>
);

const StatCard = ({ icon: Icon, title, value, sub, accent }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 font-medium">{title}</p>
      <p className="text-2xl font-bold text-gray-900 leading-tight">{value ?? "—"}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
    </div>
  </div>
);

const MigrationRow = ({ number, label, description, ready, pendingCount, onRun, running }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
            ready ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {number}
        </div>
        <div>
          <p className="font-semibold text-gray-900 text-sm">{label}</p>
          <p className="text-xs text-gray-500 mt-0.5 max-w-md">{description}</p>
          <div className="mt-2">
            {ready ? (
              <StatusBadge ok label="Up to date" />
            ) : (
              <StatusBadge ok={false} label={`${pendingCount} record(s) pending`} />
            )}
          </div>
        </div>
      </div>
      <button
        onClick={onRun}
        disabled={running}
        className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shrink-0"
      >
        {running ? (
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
        ) : (
          <PlayIcon className="w-4 h-4" />
        )}
        {running ? "Running…" : "Run"}
      </button>
    </div>
  </div>
);

// ── Log viewer ────────────────────────────────────────────────────────────────
const LogViewer = ({ lines }) => {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  if (!lines || lines.length === 0) return null;

  const getLineClass = (line) => {
    if (line.startsWith("[ERROR") || line.includes("failed") || line.includes("ERROR"))
      return "text-red-600";
    if (line.startsWith("[WARN") || line.includes("WARN"))
      return "text-yellow-600";
    if (line.includes("[OK") || line.includes("successfully") || line.includes("completed"))
      return "text-green-600";
    if (line.startsWith("[SKIP"))
      return "text-gray-400";
    if (line.includes("===") || line.includes("Migration"))
      return "text-blue-600 font-semibold";
    return "text-gray-700";
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 border-b border-gray-200">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="text-xs text-gray-400 ml-2 font-mono">migration output</span>
      </div>
      <div className="p-4 font-mono text-xs overflow-y-auto max-h-96 space-y-0.5">
        {lines.map((line, i) => (
          <p key={i} className={getLineClass(line)}>
            {line || " "}
          </p>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const Migrations = () => {
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [logLines, setLogLines] = useState([]);

  const { data: statusData, isLoading: statusLoading, error: statusError, refetch: refetchStatus } =
    useQuery({
      queryKey: ["migrationStatus"],
      queryFn: () => getMigrationStatus().then((r) => r.data.data),
      refetchOnWindowFocus: false,
    });

  const handleRun = async (migrations) => {
    const label = Array.isArray(migrations) ? migrations.join(",") : migrations;
    setRunning(label);
    setLogLines([]);
    setLastResult(null);

    try {
      const res = await runMigrations(migrations);
      const data = res.data;
      setLastResult(data);

      const allLines = [];
      if (data.results?.seed) {
        allLines.push("── System Role Seeder ──────────────────────────────");
        allLines.push(`COMPANY_OWNER: ${data.results.seed.COMPANY_OWNER || data.results.seed.error}`);
        allLines.push(`ORG_ADMIN: ${data.results.seed.ORG_ADMIN || data.results.seed.error}`);
        allLines.push("");
      }
      if (data.results?.migration001?.log) {
        allLines.push(...data.results.migration001.log);
        allLines.push("");
      }
      if (data.results?.migration002?.log) {
        allLines.push(...data.results.migration002.log);
      }

      setLogLines(allLines);

      if (data.success) {
        toast.success("Migration completed successfully");
      } else {
        toast.error("Migration completed with errors — check the log");
      }

      await refetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Migration failed";
      toast.error(msg);
      setLogLines([`[ERROR] ${msg}`]);
    } finally {
      setRunning(null);
    }
  };

  const status = statusData;
  const anyRunning = running !== null;

  const migration001Pending = status?.migration001?.companiesPendingMigration ?? 0;
  const migration002PendingA = status?.migration002?.usersWithOldCompanyField ?? 0;
  const migration002PendingB = status?.migration002?.verifiedUsersWithNoOrg ?? 0;
  const migration002Pending = migration002PendingA + migration002PendingB;

  const allReady =
    status?.systemRoles?.ORG_ADMIN &&
    status?.systemRoles?.COMPANY_OWNER &&
    status?.migration001?.ready &&
    status?.migration002?.ready;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Database Migrations</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Run and monitor data migrations for the Organization architecture.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetchStatus()}
            disabled={statusLoading}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400 rounded-lg text-sm transition-colors bg-white"
          >
            <ArrowPathIcon className={`w-4 h-4 ${statusLoading ? "animate-spin" : ""}`} />
            Refresh Status
          </button>
          <button
            onClick={() => handleRun(["all"])}
            disabled={anyRunning}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {anyRunning && running === "all" ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )}
            Run All Migrations
          </button>
        </div>
      </div>

      {/* ── Overall health banner ── */}
      {!statusLoading && status && (
        <div
          className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
            allReady
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-yellow-50 border-yellow-200 text-yellow-700"
          }`}
        >
          {allReady ? (
            <CheckCircleIcon className="w-5 h-5 shrink-0" />
          ) : (
            <ClockIcon className="w-5 h-5 shrink-0" />
          )}
          <p className="text-sm font-medium">
            {allReady
              ? "All migrations are up to date. No action required."
              : "Some migrations are pending. Review below and run the migrations."}
          </p>
        </div>
      )}

      {/* ── Stats row ── */}
      {statusLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse h-20" />
          ))}
        </div>
      ) : status ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={BuildingOfficeIcon}
            title="Organizations"
            value={status.totals?.organizations}
            sub="Total created"
            accent="bg-blue-100 text-blue-600"
          />
          <StatCard
            icon={UsersIcon}
            title="Memberships"
            value={status.totals?.memberships}
            sub="Total records"
            accent="bg-purple-100 text-purple-600"
          />
          <StatCard
            icon={CircleStackIcon}
            title="Companies Pending"
            value={migration001Pending}
            sub="Need migration 001"
            accent={migration001Pending > 0 ? "bg-yellow-100 text-yellow-600" : "bg-green-100 text-green-600"}
          />
          <StatCard
            icon={ServerStackIcon}
            title="Users Pending"
            value={migration002Pending}
            sub={`${migration002PendingA} old field · ${migration002PendingB} no org`}
            accent={migration002Pending > 0 ? "bg-yellow-100 text-yellow-600" : "bg-green-100 text-green-600"}
          />
        </div>
      ) : null}

      {statusError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          <ExclamationCircleIcon className="w-5 h-5 shrink-0" />
          Failed to load migration status. Check backend connectivity.
        </div>
      )}

      {/* ── System Roles section ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 shrink-0">
              <ShieldCheckIcon className="w-4 h-4" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">System Role Seeder</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Creates the <code className="text-indigo-600">ORG_ADMIN</code> and{" "}
                <code className="text-indigo-600">COMPANY_OWNER</code> system roles. Must run before migrations.
              </p>
              <div className="flex items-center flex-wrap gap-2 mt-2">
                <StatusBadge
                  ok={status?.systemRoles?.ORG_ADMIN}
                  label={`ORG_ADMIN ${status?.systemRoles?.ORG_ADMIN ? "exists" : "missing"}`}
                />
                <StatusBadge
                  ok={status?.systemRoles?.COMPANY_OWNER}
                  label={`COMPANY_OWNER ${status?.systemRoles?.COMPANY_OWNER ? "exists" : "missing"}`}
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => handleRun(["seed"])}
            disabled={anyRunning}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 text-sm font-medium rounded-lg transition-colors shrink-0"
          >
            {running === "seed" ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )}
            {running === "seed" ? "Running…" : "Run Seeder"}
          </button>
        </div>
      </div>

      {/* ── Migration 001 ── */}
      <MigrationRow
        number="001"
        label="Company → Organization"
        description="Migrates billing/subscription data from each Company document to a new Organization. Creates CompanyMembership records for all users and links owners to their Organization."
        ready={status?.migration001?.ready}
        pendingCount={migration001Pending}
        onRun={() => handleRun(["seed", "001"])}
        running={anyRunning && (running === "seed,001" || running === "001")}
      />

      {/* ── Migration 002 ── */}
      <MigrationRow
        number="002"
        label="Backfill User Organizations"
        description="Fixes users that have no Organization after Migration 001. Case A: links users with the old 'company' field to their migrated Organization. Case B: creates a placeholder FREE Organization for verified users with no company at all."
        ready={status?.migration002?.ready}
        pendingCount={migration002Pending}
        onRun={() => handleRun(["seed", "002"])}
        running={anyRunning && (running === "seed,002" || running === "002")}
      />

      {/* ── Log output ── */}
      {logLines.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Migration Output</h2>
            {lastResult && (
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  lastResult.success
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {lastResult.success ? "✓ Success" : "✗ Completed with errors"}
              </span>
            )}
          </div>
          <LogViewer lines={logLines} />

          {/* Summary cards */}
          {lastResult?.results && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
              {lastResult.results.migration001 && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs shadow-sm">
                  <p className="font-semibold text-gray-700 mb-2">Migration 001</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Migrated</span>
                      <span className="text-green-600 font-semibold">
                        {lastResult.results.migration001.migrated}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Skipped</span>
                      <span className="text-gray-600">
                        {lastResult.results.migration001.skipped}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Errors</span>
                      <span
                        className={
                          lastResult.results.migration001.errors > 0
                            ? "text-red-600"
                            : "text-gray-600"
                        }
                      >
                        {lastResult.results.migration001.errors}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {lastResult.results.migration002 && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs shadow-sm">
                  <p className="font-semibold text-gray-700 mb-2">Migration 002</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Case A fixed</span>
                      <span className="text-green-600 font-semibold">
                        {lastResult.results.migration002.caseA?.fixed}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Case B fixed</span>
                      <span className="text-green-600 font-semibold">
                        {lastResult.results.migration002.caseB?.fixed}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Errors</span>
                      <span
                        className={
                          (lastResult.results.migration002.caseA?.errors || 0) +
                            (lastResult.results.migration002.caseB?.errors || 0) >
                          0
                            ? "text-red-600"
                            : "text-gray-600"
                        }
                      >
                        {(lastResult.results.migration002.caseA?.errors || 0) +
                          (lastResult.results.migration002.caseB?.errors || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {lastResult.results.seed && (
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs shadow-sm">
                  <p className="font-semibold text-gray-700 mb-2">System Roles</p>
                  <div className="space-y-1">
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-400 shrink-0">COMPANY_OWNER</span>
                      <span className="text-gray-600 text-right truncate">
                        {lastResult.results.seed.COMPANY_OWNER?.includes("already")
                          ? "already exists"
                          : "created"}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-400 shrink-0">ORG_ADMIN</span>
                      <span className="text-gray-600 text-right truncate">
                        {lastResult.results.seed.ORG_ADMIN?.includes("already")
                          ? "already exists"
                          : "created"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Info footer ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-500 space-y-1">
        <p className="font-semibold text-gray-600">Notes</p>
        <p>• All migrations are <strong className="text-gray-700">idempotent</strong> — safe to run multiple times without duplicating data.</p>
        <p>• Always run the <strong className="text-gray-700">Seeder</strong> before running migration 001 or 002 (it ensures the required system roles exist).</p>
        <p>• Migration 001 reads billing data directly from MongoDB and moves it to Organization documents.</p>
        <p>• Migration 002 handles two edge cases: users with an old company link and verified users with no organization at all.</p>
        <p>• The standalone CLI versions can also be run on the server: <code className="text-indigo-600">node migrations/001_company_to_organization.js</code></p>
      </div>
    </div>
  );
};

export default Migrations;
